<template>
  <v-dialog
    :model-value="modelValue"
    max-width="640"
    persistent
    scrollable
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="pa-2">
      <v-card-title class="text-h6 text-wrap">
        {{
          $t("waste.send_out.title", { name: container ? container.name : "" })
        }}
      </v-card-title>

      <v-card-text>
        <v-alert type="info" variant="tonal" density="compact" class="mb-4">
          {{ $t("waste.send_out.info") }}
        </v-alert>
        <v-alert
          v-if="alert"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-4"
        >
          {{ alert }}
        </v-alert>

        <div class="text-subtitle-2 mb-1">
          {{ $t("waste.send_out.weight") }} *
        </div>
        <v-radio-group
          v-model="form.source"
          :disabled="saving"
          density="comfortable"
          hide-details
          class="mb-2"
        >
          <v-radio
            v-if="latest && latest.fresh"
            value="latest"
            :label="
              $t('waste.send_out.use_latest', {
                kg: formatKg(latest.netKg),
                date: formatDate(latest.weighedAt),
              })
            "
          />
          <v-radio value="new" :label="$t('waste.send_out.new_weighing')" />
        </v-radio-group>
        <v-alert
          v-if="latest && !latest.fresh"
          type="warning"
          variant="tonal"
          density="compact"
          class="mb-3"
        >
          {{
            $t("waste.send_out.latest_stale", {
              kg: formatKg(latest.netKg),
              date: formatDate(latest.weighedAt),
            })
          }}
        </v-alert>
        <div
          v-if="fieldMessage('weighing')"
          class="text-caption text-error mb-2"
        >
          {{ fieldMessage("weighing") }}
        </div>

        <container-weighing-fields
          v-if="form.source === 'new'"
          v-model="form.weighing"
          :container="container"
          :errors="weighingFieldErrors"
          :server-errors="weighingServerErrors"
          :disabled="saving"
          :now="now"
          class="mb-2"
        />

        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="form.recipient"
              variant="outlined"
              density="comfortable"
              maxlength="200"
              :label="`${$t('waste.send_out.recipient')} *`"
              :error-messages="fieldMessage('recipient')"
              :disabled="saving"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="form.transporter"
              variant="outlined"
              density="comfortable"
              maxlength="200"
              :label="$t('waste.send_out.transporter')"
              :error-messages="fieldMessage('transporter')"
              :disabled="saving"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="form.date"
              type="date"
              variant="outlined"
              density="comfortable"
              :max="today"
              :min="openedDay"
              :label="`${$t('waste.send_out.date')} *`"
              :error-messages="fieldMessage('date')"
              :disabled="saving"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-file-input
              :model-value="form.file ? [form.file] : []"
              variant="outlined"
              density="comfortable"
              accept="application/pdf,image/jpeg,image/png,image/webp"
              prepend-icon=""
              prepend-inner-icon="mdi-paperclip"
              :label="$t('waste.send_out.document')"
              :error-messages="fieldMessage('file')"
              :disabled="saving"
              @update:model-value="setFile"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          variant="outlined"
          color="primary"
          :disabled="saving"
          @click="close"
        >
          {{ $t("waste.form.cancel") }}
        </v-btn>
        <v-btn
          variant="flat"
          color="primary"
          :loading="saving"
          :disabled="!valid || saving"
          @click="save"
        >
          {{ $t("waste.send_out.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// "Invia (svuota)" on the container page (Stage 6 slice 3, L7, D6): Manager / Admin (the API
// checks the role again). The weight is the latest weighing when it was taken after the last
// entry, otherwise a new weighing written in the same command. Recipient, optional transporter,
// the date (Rome, today at most, not before the filling opened) and an optional document. An
// internal record: no FIR, no RENTRI. After it the container starts again empty.
import { newCommandId, recordWasteSendOut } from "@/api/compliance";
import ContainerWeighingFields from "@/components/Waste/containerWeighingFields.vue";
import {
  buildSendOut,
  emptySendOutForm,
  latestWeighingOption,
  romeDay,
  sendOutErrors,
  serverErrorField,
} from "@/components/Waste/sendOutForm.mjs";
import { formatDate, formatNumber } from "@/components/Waste/wasteList.mjs";

const PREFIX = "weighing.";

export default {
  name: "WasteSendOutDialog",
  components: { ContainerWeighingFields },
  props: {
    modelValue: { type: Boolean, default: false },
    container: { type: Object, default: null },
    revision: { type: Number, default: 0 },
  },
  emits: ["update:modelValue", "saved", "conflict"],
  data: () => ({
    form: emptySendOutForm(null),
    now: new Date(),
    // Field -> message from the API, cleared when the form changes.
    serverErrors: {},
    // A field the user has touched, or every field after a first Save: only then a "required".
    tried: false,
    alert: "",
    saving: false,
    pendingCommandId: "",
  }),
  computed: {
    latest() {
      return latestWeighingOption(this.container);
    },
    errors() {
      return sendOutErrors(this.form, this.container, this.now);
    },
    valid() {
      return Object.keys(this.errors).length === 0;
    },
    today() {
      return romeDay(this.now);
    },
    openedDay() {
      const filling = (this.container && this.container.filling) || {};
      return filling.openedAt ? romeDay(filling.openedAt) : undefined;
    },
    weighingFieldErrors() {
      return this.unprefixed(this.errors);
    },
    weighingServerErrors() {
      return this.unprefixed(this.serverErrors);
    },
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(open) {
        this.stopClock();
        if (!open) return;
        this.now = new Date();
        this.form = emptySendOutForm(this.container, this.now);
        this.serverErrors = {};
        this.alert = "";
        this.tried = false;
        this.pendingCommandId = newCommandId();
        this.clock = setInterval(() => {
          this.now = new Date();
        }, 30000);
      },
    },
    form: {
      deep: true,
      handler() {
        this.serverErrors = {};
      },
    },
    // After a reload (a conflict) the latest weighing may no longer be usable.
    latest(option) {
      if (this.form.source === "latest" && !(option && option.fresh)) {
        this.form.source = "new";
      }
    },
  },
  created() {
    this.clock = null;
  },
  beforeUnmount() {
    this.stopClock();
  },
  methods: {
    stopClock() {
      if (this.clock) clearInterval(this.clock);
      this.clock = null;
    },
    unprefixed(errors) {
      return Object.fromEntries(
        Object.entries(errors)
          .filter(([field]) => field.startsWith(PREFIX))
          .map(([field, value]) => [field.slice(PREFIX.length), value]),
      );
    },
    formatKg(value) {
      return formatNumber(value, this.$i18n.locale);
    },
    formatDate(value) {
      return formatDate(value, this.$i18n.locale);
    },
    setFile(value) {
      const list = Array.isArray(value) ? value : [value];
      this.form.file = list.find(Boolean) || null;
    },
    fieldMessage(field) {
      if (this.serverErrors[field]) return this.serverErrors[field];
      const reason = this.errors[field];
      if (!reason) return "";
      if (reason === "required") {
        return this.tried
          ? this.$t("acceptance.weighings.errors.required")
          : "";
      }
      return this.$t(`waste.send_out.errors.${reason}`);
    },
    close() {
      this.$emit("update:modelValue", false);
    },
    async save() {
      this.now = new Date();
      this.tried = true;
      if (!this.valid || this.saving) return;
      this.saving = true;
      this.alert = "";
      try {
        const detail = await recordWasteSendOut(
          this.container.id,
          this.revision,
          this.pendingCommandId,
          buildSendOut(this.form, this.container),
        );
        this.$emit("saved", detail);
        this.close();
      } catch (err) {
        const data = (err && err.response && err.response.data) || {};
        if (data.code === "revision_conflict") {
          this.alert = this.$t("waste.send_out.conflict");
          this.$emit("conflict");
        } else if (data.code === "weighing_stale") {
          // Someone recorded an entry after the weighing: weigh again (the page reloads).
          this.alert = this.$t("waste.send_out.stale");
          this.form.source = "new";
          this.$emit("conflict");
        } else {
          const field = serverErrorField(data);
          const known =
            field &&
            (field.startsWith(PREFIX) ||
              ["weighing", "recipient", "transporter", "date", "file"].includes(
                field,
              ));
          const message =
            data.errorMsg ||
            (err && err.message) ||
            this.$t("waste.load_error");
          if (known) this.serverErrors = { [field]: message };
          else this.alert = message;
        }
        // A refused save is a new attempt next time; after a network error the same commandId is
        // kept, so a retry that already reached the API is answered as a replay.
        if (err && err.response) this.pendingCommandId = newCommandId();
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
